// ---------------------------------------------------------------------------
// Slide content types for the curriculum builder's slide resource kind.
//
// Schema follows the DocumentContentV1 envelope pattern: the top-level object
// stored in `resources.content` is `SlideContentV1` which wraps a versioned
// presentation payload.
//
// Canvas coordinate system: origin top-left, units in CSS pixels.
// Default page size: 960 × 540 px (16:9).
// ---------------------------------------------------------------------------

// -- Templates (Google Slides–aligned names) --------------------------------

export type SlideTemplate = 'BLANK' | 'TITLE' | 'TITLE_AND_BODY' | 'TITLE_AND_TWO_COLUMNS';

// -- Design tokens (theme references) ---------------------------------------

/** The 9 palette color tokens (§4.2). A slide/layout field may carry `$<name>`. */
export type ColorToken =
  | 'primary'
  | 'primaryContrast'
  | 'secondary'
  | 'secondaryContrast'
  | 'background'
  | 'surface'
  | 'text'
  | 'textMuted'
  | 'accent';

/** The 2 font tokens (§4.3). */
export type FontToken = 'headingFont' | 'bodyFont';

/** Named content zone inside a layout. Placeholder ids are UPPERCASE, matching
 *  Google Slides placeholder types and the layout-key casing. */
export type PlaceholderId = 'TITLE' | 'SUBTITLE' | 'BODY' | 'LEFT_BODY' | 'RIGHT_BODY';

// -- Text alignment ---------------------------------------------------------

export type SlideTextAlign = 'left' | 'center' | 'right' | 'justify';
export type SlideVerticalAlign = 'top' | 'middle' | 'bottom';

// -- Element positioning ----------------------------------------------------

/** Absolute position & size in CSS-pixel canvas coordinates. */
export interface ElementPosition {
  x: number;
  y: number;
  /** Minimum 20 */
  width: number;
  /** Minimum 20 */
  height: number;
  /** Degrees, normalised to [0, 360). Default 0. Clockwise, applied about the
   *  element's center (matches CSS `rotate()` with the default transform-origin). */
  rotation: number;
  /** Mirror horizontally (about the vertical center axis). Default false.
   *  Absent ⇒ not flipped. A reflection cannot be expressed as a rotation, so
   *  imports of mirrored elements set this. */
  flipH?: boolean;
  /** Mirror vertically (about the horizontal center axis). Default false. */
  flipV?: boolean;
  /** Stacking order. Default 1. */
  zIndex: number;
}

/** Stored elements carry a PARTIAL position override — e.g. a z-order edit stores
 *  only `{ zIndex }` (§8.2). Missing fields are completed at resolve time from the
 *  placeholder default (for `placeholderId`-bound elements) or the editor
 *  hard-default (for free elements). `PlaceholderDefinition.position` and
 *  `ResolvedSlideElement.position` remain the full `ElementPosition`. */
export type ElementPositionOverride = Partial<ElementPosition>;

// -- Text style -------------------------------------------------------------

export interface SlideTextStyle {
  fontFamily?: string;
  /** Points. Default 22. Range [8, 200]. */
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
  lineHeight?: number;
  textAlign?: SlideTextAlign;
  /** Vertical alignment of the text within its box. Default 'top'. Mirrors
   *  Google Slides' shape `contentAlignment` (TOP/MIDDLE/BOTTOM). */
  verticalAlign?: SlideVerticalAlign;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none';
  borderRadius?: number;
  /** Inner padding in px */
  padding?: number;
}

// -- Element types (discriminated union) ------------------------------------

interface SlideElementBase {
  id: string;
  /** Optional partial override. Absent / partial geometry is completed at resolve
   *  time from the placeholder default (for `placeholderId`-bound elements) or the
   *  editor hard-default (for free elements). The resolved element always carries a
   *  full `ElementPosition` (§6.2, §8.2). */
  position?: ElementPositionOverride;
  locked?: boolean;
}

export interface TextElement extends SlideElementBase {
  type: 'text';
  /** Binds the element to a layout placeholder; geometry/style are inherited from
   *  the matching `PlaceholderDefinition` and overridden per-field. Text-only:
   *  Phase 1 placeholders carry only `textStyle`. */
  placeholderId?: PlaceholderId;
  content: {
    /** Plain-text fallback / initial value */
    text: string;
    /** TipTap JSON document for rich inline formatting */
    tiptapDoc?: Record<string, unknown>;
  };
  /** Omitted ⇒ inherit the placeholder's style; present ⇒ override per-field
   *  (unset fields still inherit). */
  style?: SlideTextStyle;
}

/** A rectangle corner: top-left, top-right, bottom-right, bottom-left. */
export type ShapeCorner = 'tl' | 'tr' | 'br' | 'bl';

export const SHAPE_CORNERS: readonly ShapeCorner[] = ['tl', 'tr', 'br', 'bl'];

export interface ShapeElement extends SlideElementBase {
  type: 'shape';
  content: {
    shapeType: 'rectangle' | 'circle' | 'line';
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    borderRadius?: number;
    roundedCorners?: ShapeCorner[];
  };
}

export interface ImageElement extends SlideElementBase {
  type: 'image';
  content: {
    /** Resolved image URL (http/https) */
    src?: string;
    /** Un-resolved placeholder search term */
    imageQuery?: string;
    alt?: string;
    /** Storage bucket name when backed by Supabase Storage. */
    storageBucket?: string;
    /** Storage path within the bucket. */
    storagePath?: string;
    /** How the image fills its bounding box. Default 'cover'. */
    objectFit?: 'fill' | 'cover' | 'contain';
    crop?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
  };
}

// -- Interactive widgets ----------------------------------------------------

/** Supported interactive widget types.  Extend this union to add new widgets. */
export type InteractiveWidgetType = 'calculator' | 'timer';

export interface InteractiveElement extends SlideElementBase {
  type: 'interactive';
  content: {
    widgetType: InteractiveWidgetType;
    /** Widget-specific configuration (varies per widgetType). */
    config: Record<string, unknown>;
  };
}

// -- Video ------------------------------------------------------------------

/** Source discriminator. Locks the schema shape so adding Drive/upload later
 *  is purely additive (no migration of existing JSON). */
export type VideoSource = 'youtube';

export interface VideoElement extends SlideElementBase {
  type: 'video';
  content: {
    source: 'youtube';
    /** Resolved YouTube video ID. */
    videoId?: string;
    /** Cached from resolve for editor display. */
    title?: string;
    channelName?: string;
    /** Cached; falls back to i.ytimg.com URL at render time. */
    thumbnailUrl?: string;
    startSeconds?: number;
    endSeconds?: number;
    autoplay?: boolean;
    mute?: boolean;
    forceCaptions?: boolean;
    /** Description for screen readers. */
    alt?: string;
  };
}

export type SlideElement =
  | TextElement
  | ShapeElement
  | ImageElement
  | InteractiveElement
  | VideoElement;

// -- Theme (design tokens + layouts) ----------------------------------------

/** A named content zone in a layout: default geometry + (optional) default text
 *  style. A slide element binds via `placeholderId`. */
export interface PlaceholderDefinition {
  placeholderId: PlaceholderId;
  /** Placeholder default geometry (full position). */
  position: ElementPosition;
  /** Default text style: font token (or raw), raw size (pt), weight, align,
   *  color token (or raw hex). */
  textStyle?: SlideTextStyle;
}

/** A decoration element is a normal element shape with token refs allowed.
 *  Rendered at resolve time, never stored within `slide.elements`. Phase 1's
 *  DEFAULT theme ships none. */
export type DecorationElement =
  | (Omit<ShapeElement, 'id'> & { id?: string })
  | (Omit<TextElement, 'id'> & { id?: string })
  | (Omit<ImageElement, 'id'> & { id?: string });

/** The theme's concrete design for one `SlideTemplate`. */
export interface LayoutDefinition {
  /** Color token (e.g. `$background`) or raw hex. */
  background: string;
  placeholders: PlaceholderDefinition[];
  /** May be []; Phase 1 DEFAULT theme uses [] everywhere. */
  decoration: DecorationElement[];
}

/** The deck-embedded, fully-resolved theme — the render source of truth. */
export interface ResolvedTheme {
  /** Provenance — which registry theme produced this (always 'default' in Phase 1). */
  themeId: string;
  name: string;
  /** All 9 color tokens → hex. */
  palette: Record<ColorToken, string>;
  /** Both font tokens → CSS family strings. */
  fonts: Record<FontToken, string>;
  /** Every template key present. */
  layouts: Record<SlideTemplate, LayoutDefinition>;
}

// -- Slide ------------------------------------------------------------------

export interface Slide {
  id: string;
  /** Defaults to 'BLANK' when absent. */
  template?: SlideTemplate;
  /** Optional — a template with no placeholder content can render with none. */
  elements?: SlideElement[];
  /** Optional — absent inherits the layout background (§6.2); token or raw. */
  background?: {
    color: string;
  };
}

// -- Presentation -----------------------------------------------------------

export interface SlidePresentation {
  id: string;
  title: string;
  /** Absent ⇒ un-themed deck (render raw via the `DEFAULT_THEME` fallback). */
  theme?: ResolvedTheme;
  slides: Slide[];
  pageSize: {
    width: number;
    height: number;
  };
}

// -- Content envelope (stored in resources.content) -------------------------

export interface SlideContentV1 {
  schemaVersion: '1';
  presentation: SlidePresentation;
}

// -- Layout template metadata (used by SlideLayoutPicker) -------------------

export interface SlideTemplateInfo {
  template: SlideTemplate;
  name: string;
  description: string;
}

// -- Constants --------------------------------------------------------------

export const SLIDE_PAGE_WIDTH = 960;
export const SLIDE_PAGE_HEIGHT = 540;
export const MIN_ELEMENT_SIZE = 20;
export const DEFAULT_SLIDE_BODY_FONT_SIZE_PT = 22;
export const DEFAULT_SLIDE_BODY_LINE_HEIGHT = 1.4;
export const DEFAULT_SLIDE_TEXT_PADDING_PX = 9.6;
